import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';
import MessageContextMenu from './MessageContextMenu';
import api from '../../api';

export const MessageItem = ({
  msg,
  avatarImage,
  aiAssistantName,
  groupJoinRequest,
  friendRequest,
  onAcceptJoin,
  onRejectJoin,
  onViewChatLog,
  onIgnoreFriend,
  onAcceptFriend,
  onIgnoreChatLog,
  userId,
  onMessageDeleted,
  chatType = 'private'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 } });
  const audioRef = useRef(null);
  const isMe = msg.sender === 'me';
  const isJoinRequest = msg.isJoinRequest;
  const isFriendRequest = msg.isFriendRequest;
  const isChatLogRequest = msg.isChatLogRequest;
  const isVoice = msg.voiceData;
  const isImage = msg.isImage;

  const isRecalled = msg.recalled || msg.content === '消息已撤回';
  const canRecall = isMe && !isRecalled && msg.createdAt && 
    (new Date() - new Date(msg.createdAt)) / (1000 * 60) <= 2;

  useEffect(() => {
    if (msg.voiceData) {
      audioRef.current = new Audio(msg.voiceData);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [msg.voiceData]);

  const handleLongPress = (e) => {
    e.preventDefault();
    triggerHaptic();
    
    const touch = e.touches ? e.touches[0] : e;
    const rect = e.currentTarget.getBoundingClientRect();
    
    setContextMenu({
      isOpen: true,
      position: {
        x: touch.clientX || rect.left + rect.width / 2,
        y: touch.clientY || rect.top
      }
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content).then(() => {
      alert('已复制到剪贴板');
    });
  };

  const handleDelete = async () => {
    try {
      if (chatType === 'group') {
        await api.deleteGroupMessage(msg.groupId || msg.chatId, msg.id, userId);
      } else {
        await api.deleteMessage(msg.id, userId);
      }
      if (onMessageDeleted) onMessageDeleted(msg.id);
    } catch (err) {
      alert('删除失败: ' + err.message);
    }
  };

  const handleRecall = async () => {
    try {
      if (chatType === 'group') {
        await api.recallGroupMessage(msg.groupId || msg.chatId, msg.id, userId);
      } else {
        await api.recallMessage(msg.id, userId);
      }
      if (onMessageDeleted) onMessageDeleted(msg.id);
    } catch (err) {
      alert('撤回失败: ' + err.message);
    }
  };

  const handleAcceptFriend = () => {
    triggerHaptic();
    if (onAcceptFriend) {
      onAcceptFriend(msg.requestData || msg);
    }
  };

  const handleIgnoreFriend = () => {
    triggerHaptic();
    if (onIgnoreFriend) {
      onIgnoreFriend(msg.requestData || msg);
    }
  };

  const handleAction = () => {
    triggerHaptic();
  };

  const togglePlayVoice = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isRecalled) {
    return (
      <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className="text-xs text-gray-500 italic px-4 py-2">
          消息已撤回
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-4`}
        onContextMenu={handleLongPress}
        onTouchStart={(e) => {
          const timer = setTimeout(() => handleLongPress(e), 500);
          e.target.addEventListener('touchend', () => clearTimeout(timer), { once: true });
        }}
      >
        {/* Message content */}
        <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
          {/* Join Request */}
          {isJoinRequest && groupJoinRequest && (
            <div className="glass-card p-4 rounded-2xl w-full">
              <p className="text-sm text-gray-300 mb-3">
                {msg.text}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { handleAction(); onRejectJoin(); }}
                  className="flex-1 py-2 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                >
                  拒绝
                </button>
                <button
                  onClick={() => { handleAction(); onAcceptJoin(); }}
                  className="flex-1 py-2 rounded-xl bg-[var(--neon-primary)] text-black text-sm font-bold hover:opacity-90 transition-opacity"
                >
                  同意
                </button>
              </div>
            </div>
          )}

          {/* Friend Request */}
          {isFriendRequest && (
            <div className="glass-card p-4 rounded-2xl w-full">
              <p className="text-sm text-gray-300 mb-3">{msg.text}</p>
              <div className="flex gap-2">
                <button
                  onClick={handleIgnoreFriend}
                  className="flex-1 py-2 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                >
                  忽略
                </button>
                <button
                  onClick={handleAcceptFriend}
                  className="flex-1 py-2 rounded-xl bg-[var(--neon-primary)] text-black text-sm font-bold hover:opacity-90 transition-opacity"
                >
                  接受
                </button>
              </div>
            </div>
          )}

          {/* Chat Log Request */}
          {isChatLogRequest && (
            <div className="glass-card p-4 rounded-2xl w-full">
              <p className="text-sm text-gray-300 mb-3">{msg.text}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { handleAction(); onIgnoreChatLog(); }}
                  className="flex-1 py-2 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                >
                  忽略
                </button>
                <button
                  onClick={() => { handleAction(); onViewChatLog(); }}
                  className="flex-1 py-2 rounded-xl bg-[var(--neon-primary)] text-black text-sm font-bold hover:opacity-90 transition-opacity"
                >
                  查看
                </button>
              </div>
            </div>
          )}

          {/* Regular Message */}
          {!isJoinRequest && !isFriendRequest && !isChatLogRequest && (
            <>
              {!isMe && (
                <div className="flex items-center gap-2 mb-1">
                  <img
                    src={msg.isAI ? avatarImage : 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + msg.sender}
                    alt="Avatar"
                    className="w-6 h-6 rounded-full"
                  />
                  <span className="text-xs text-gray-400">
                    {msg.isAI ? aiAssistantName : msg.sender}
                  </span>
                </div>
              )}
              
              <div
                className={`rounded-2xl px-4 py-2.5 ${
                  isMe
                    ? 'bg-[var(--neon-primary)] text-black rounded-br-md'
                    : 'glass-card text-white rounded-bl-md'
                } ${isVoice ? 'min-w-[120px]' : ''}`}
              >
                {isVoice ? (
                  <button
                    onClick={togglePlayVoice}
                    className="flex items-center gap-2 w-full"
                  >
                    {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                    <span className="text-sm">
                      {formatDuration(msg.voiceDuration || 0)}
                    </span>
                  </button>
                ) : isImage ? (
                  <img
                    src={msg.imageData}
                    alt="Shared"
                    className="max-w-full rounded-lg cursor-pointer"
                    onClick={() => setIsImageEnlarged(true)}
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                )}
              </div>

              <span className="text-[10px] text-gray-500 mt-1">
                {new Date(msg.timestamp || msg.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
            </>
          )}
        </div>

        {/* Enlarged Image Modal */}
        {isImageEnlarged && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setIsImageEnlarged(false)}
          >
            <img
              src={msg.imageData}
              alt="Enlarged"
              className="max-w-full max-h-full rounded-lg"
            />
          </div>
        )}
      </div>

      {/* Context Menu */}
      <MessageContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onRecall={handleRecall}
        canRecall={canRecall}
        isRecalled={isRecalled}
      />
    </>
  );
};

export default MessageItem;
